import React, { useMemo } from "react";
import { AlertCircle, Database } from "react-feather";
import { Spinner } from "../../components/atoms/Spinner";
import { DataStudioProfileTable } from "../../components/organisms/DataStudioProfileTable";
import { DataStudioRecommendationsPanel } from "../../components/organisms/DataStudioRecommendationsPanel";
import { useDataStudioTableSelection } from "../../hooks/useDataStudioTablesPanel";
import { useDataStudioTableProfile } from "../../hooks/useDataStudioTableProfile";
import { useDataStudioTableProfileColumns } from "../../hooks/useDataStudioTableProfileColumns";
import { useDataStudioTableProfileRows } from "../../hooks/useDataStudioTableProfileRows";
import { countClassifications } from "../../services/dataStudio/dataStudioColumnProfileMappers";
import {
  PageEmptyState,
  PageStateRegion,
  PageSurface,
} from "./DataStudioPage.styles";
import { usesLiveSample } from "../../services/dataStudio/dataStudioLiveSampleMappers";
import type { DataStudioPageProps } from "./DataStudioPage.types";
import { useDataStudioWorkspaceInternalState } from "./useDataStudioWorkspaceInternalState";

const EMPTY_ROW_PAGE_INFO = {
  total: 0,
  nextOffset: null,
  hasMore: false,
  previousOffset: null,
  hasPrevious: false,
};

export const DataStudioPage: React.FC<DataStudioPageProps> = ({
  tables = [],
  isLoading = false,
  errorMessage = null,
}) => {
  const { selectedTable } = useDataStudioTableSelection(tables);

  /**
   * An asset is addressed by `(connectionId, assetId)`. The connection id
   * travels on the selected row because the asset search spans connectors —
   * it is not the route's connection.
   */
  const identity = useMemo(
    () =>
      selectedTable?.source.id
        ? {
            connectionId: selectedTable.source.id,
            assetId: selectedTable.id,
            // Sheets and SaaS objects have no stored columns: they load from the live sample.
            liveSampleSlug: usesLiveSample(selectedTable.assetType) ? selectedTable.source.key : null,
          }
        : null,
    [selectedTable],
  );

  const profileQuery = useDataStudioTableProfile(identity);
  const columnsQuery = useDataStudioTableProfileColumns(identity);
  /**
   * The rows endpoint pages columns independently from the columns endpoint
   * (its own `column_offset`/`column_limit`, defaulting to a 50-column
   * window regardless of how far the grid has scrolled horizontally) — so
   * `columnsQuery.columnWindow` is threaded through to keep a row's cells
   * covering the columns the grid actually has loaded.
   */
  const rowsQuery = useDataStudioTableProfileRows(
    identity,
    columnsQuery.columnWindow ?? null,
  );
  const { recommendationsOpen, closeRecommendations } =
    useDataStudioWorkspaceInternalState(selectedTable?.id ?? null);

  /**
   * Header metadata comes from the asset endpoint; columns and preview rows
   * each stream from their own endpoint independently via their own infinite
   * queries, so the grid paints its title/quality summary before either page
   * lands. `classificationCounts`/`columnCount` are recomputed from whatever
   * columns have loaded so far rather than trusted from the asset-only
   * profile response — they are therefore approximate (reflect the loaded
   * window, not necessarily the whole table) until the asset itself reports
   * `column_count`, which wins via the `||` fallback below.
   */
  const profile = useMemo(() => {
    if (!profileQuery.profile) return null;

    return {
      ...profileQuery.profile,
      table: {
        ...profileQuery.profile.table,
        columnCount:
          profileQuery.profile.table.columnCount || columnsQuery.columns.length,
        classificationCounts: countClassifications(columnsQuery.columns),
      },
      columns: columnsQuery.columns,
      rows: rowsQuery.rows,
      rowPageInfo: rowsQuery.rowPageInfo ?? EMPTY_ROW_PAGE_INFO,
    };
  }, [profileQuery.profile, columnsQuery.columns, rowsQuery.rows, rowsQuery.rowPageInfo]);

  /** The profile's own failure outranks the list's — it is what the user sees. */
  const visibleErrorMessage = profileQuery.errorMessage ?? errorMessage;
  const isBusy = isLoading || profileQuery.isLoading;

  const renderWorkspaceContent = () => {
    if (profile) {
      return (
        <>
          <DataStudioProfileTable
            profile={profile}
            isLoadingMoreRows={rowsQuery.isFetchingNextPage}
            onLoadMoreRows={rowsQuery.loadMore}
            isRefreshingRowsForColumns={rowsQuery.isRefreshingColumns}
            isLoadingOlderRows={rowsQuery.isLoadingOlderRows}
            onLoadOlderRows={rowsQuery.loadOlder}
            hasOlderRows={rowsQuery.hasOlderRows}
            rowsErrorMessage={rowsQuery.errorMessage}
            hasMoreColumns={columnsQuery.columnPageInfo?.hasMore ?? false}
            isLoadingMoreColumns={columnsQuery.isFetchingNextPage}
            onLoadMoreColumns={columnsQuery.loadMore}
            isLoadingOlderColumns={columnsQuery.isLoadingOlderColumns}
            onLoadOlderColumns={columnsQuery.loadOlder}
            hasOlderColumns={columnsQuery.hasOlderColumns}
            columnsErrorMessage={columnsQuery.errorMessage}
          />
          {recommendationsOpen ? (
            <DataStudioRecommendationsPanel
              connectionId={identity?.connectionId ?? ""}
              assetId={profile.table.id}
              onClose={closeRecommendations}
            />
          ) : null}
        </>
      );
    }

    if (isBusy) {
      return (
        <PageStateRegion role="status" aria-live="polite">
          <PageEmptyState
            icon={<Spinner size={32} />}
            caption="Loading table preview..."
          />
        </PageStateRegion>
      );
    }

    if (visibleErrorMessage) {
      return (
        <PageStateRegion role="alert">
          <PageEmptyState
            icon={<AlertCircle />}
            caption={visibleErrorMessage}
          />
        </PageStateRegion>
      );
    }

    return (
      <PageStateRegion role="status">
        <PageEmptyState
          icon={<Database />}
          caption="No ingested tables are available to preview."
        />
      </PageStateRegion>
    );
  };

  return (
    <PageSurface
      $hasProfile={Boolean(profile)}
      $hasRecommendations={Boolean(profile && recommendationsOpen)}
      aria-label="Data studio workspace"
      data-surface-variant={profile ? "profile" : "state"}
      data-testid="data-studio-page"
    >
      {renderWorkspaceContent()}
    </PageSurface>
  );
};

export default DataStudioPage;
